import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { API_BASE_URL, resolveFileUrl } from '../config/api';
import { lienzoFirmaAPdf, pdfALienzoFirma } from '../utils/coordenadasFirma';
import { generateTempId } from '../utils/tempId';
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Clock, Download, Save, Upload, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import PDFViewer from '../components/PDFViewer'
import SignaturePanel from '../components/SignaturePanel'
import FirmantesSelector from '../components/FirmantesSelector'
import VisualizadoresSelector from '../components/VisualizadoresSelector'
import HelpButton from '../components/HelpButton'
import EditorNewsBanner from '../components/EditorNewsBanner'
import {
  NEWS_EDITOR_VIEWER_ZOOM,
  shouldShowNewsTour,
  isNewsEditorViewerZoomSnoozed,
} from '../config/newsTours'
import { usePageTour } from '../hooks/usePageTour'
import pdfjsLib from '../utils/pdfjsWrapper.js'
import { clearAllPdfSignerEditorDrafts } from '../utils/editorDraft'
import {
  SEND_STATUS,
  createClientOperationId,
  fetchSendOperationStatus,
  postSendNotifications,
  shouldBlockResend,
  shouldClearEditorDraft,
  shouldOpenShareModal,
  shouldReuseClientOperationId,
  toastKindForStatus,
  userMessageForStatus,
} from '../utils/sendDocumentResult'
import { interpretarRespuestaFirmante, parseApiDetail } from '../utils/bloqueoFirmaUi'
// El worker ya está configurado en main.jsx y en el wrapper

const PDFEditor = () => {
  const location = useLocation()
  const navigateOriginal = useNavigate()
  const { token } = useParams()
  const { user } = useAuth()
  
  // Refs para bloquear navegaciones cuando el modal está abierto
  const showShareModalRef = useRef(false)
  const navigationBlockedRef = useRef(false)
  const sendClientOperationIdRef = useRef(null)
  const registeredDocumentoIdRef = useRef(null)
  /** Bloqueo de interfaz hasta finalizar TODO el flujo de envío (complementa idempotencia de BD). */
  const sendInFlightRef = useRef(false)
  const [sendBlockedUnknown, setSendBlockedUnknown] = useState(false)
  const [sendRegisteredPendingSmtp, setSendRegisteredPendingSmtp] = useState(false)
  
  // Interceptar navigate para bloquear cuando el modal está abierto
  const navigate = React.useCallback((path, options) => {
    // Verificar múltiples condiciones para asegurar que el modal no esté abierto
    if (navigationBlockedRef.current || showShareModalRef.current) {
      console.log('[DEBUG PDFEditor] 🚫 BLOQUEANDO navegación directa a', path, '- modal está abierto')
      console.log('[DEBUG PDFEditor] 🚫 Estado del bloqueo:', {
        navigationBlockedRef: navigationBlockedRef.current,
        showShareModalRef: showShareModalRef.current
      })
      // Lanzar un error para que el código que llama sepa que la navegación fue bloqueada
      return Promise.reject(new Error('Navegación bloqueada: modal de compartir está abierto'))
    }
    console.log('[DEBUG PDFEditor] ✅ Navegando a', path)
    navigateOriginal(path, options)
  }, [navigateOriginal])
  
  // También interceptar navigateOriginal directamente usando un wrapper
  const navigateOriginalWrapper = React.useCallback((path, options) => {
    if (navigationBlockedRef.current || showShareModalRef.current) {
      console.log('[DEBUG PDFEditor] 🚫 BLOQUEANDO navigateOriginal a', path, '- modal está abierto')
      return
    }
    return navigateOriginal(path, options)
  }, [navigateOriginal])
  
  // Función wrapper para navigate que bloquea cuando el modal está abierto
  const safeNavigate = (path, options) => {
    // Verificar múltiples condiciones para asegurar que el modal no esté abierto
    if (navigationBlockedRef.current || showShareModalRef.current) {
      console.log('[DEBUG PDFEditor] 🚫 BLOQUEANDO navegación a', path, '- modal está abierto')
      console.log('[DEBUG PDFEditor] 🚫 Estado del bloqueo:', {
        navigationBlockedRef: navigationBlockedRef.current,
        showShareModalRef: showShareModalRef.current
      })
      return
    }
    console.log('[DEBUG PDFEditor] ✅ Navegando a', path)
    navigate(path, options)
  }
  const [pdfFile, setPdfFile] = useState(null)
  
  // Verificar permisos: supervisor no puede subir documentos (solo firmar documentos compartidos)
  const isSupervisor = user?.role === 'supervisor'
  const isAdmin = user?.role === 'administrador' || user?.is_admin
  const isUser = user?.role === 'usuario'
  const [pdfInfo, setPdfInfo] = useState(null)
  const [pdfDoc, setPdfDoc] = useState(null) // Objeto PDF de PDF.js para obtener páginas
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [signatures, setSignatures] = useState([])
  const [isSigning, setIsSigning] = useState(false)
  const [showSignaturePanel, setShowSignaturePanel] = useState(false)
  const [fileId, setFileId] = useState(null)
  const [pdfScale, setPdfScale] = useState(1)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [signedDocumentUrl, setSignedDocumentUrl] = useState(null)
  const [documentHash, setDocumentHash] = useState(null)
  const [firmantes, setFirmantes] = useState([])
  const [camposFirma, setCamposFirma] = useState([])
  const [firmaSeleccionada, setFirmaSeleccionada] = useState(null)
  /** Política del documento al firmar: libre_v1 | campos_v1 (viene del backend). */
  const [politicaFirma, setPoliticaFirma] = useState(null)
  const [visualizadores, setVisualizadores] = useState([])
  const [observaciones, setObservaciones] = useState('')
  const [notas, setNotas] = useState([])
  const [notasRegistradas, setNotasRegistradas] = useState([])
  const [guardandoNotas, setGuardandoNotas] = useState(false)
  const [isDocumentSigned, setIsDocumentSigned] = useState(false)
  const [isSendingNotifications, setIsSendingNotifications] = useState(false)
  const [isSigningMode, setIsSigningMode] = useState(false)
  const [signingToken, setSigningToken] = useState(null)
  const [firmanteInfo, setFirmanteInfo] = useState(null)
  const [filePath, setFilePath] = useState(null)
  const [isSearchingUsers, setIsSearchingUsers] = useState(false)
  const [isLoadingSigningDocument, setIsLoadingSigningDocument] = useState(false)
  const [isCalibrationMode, setIsCalibrationMode] = useState(false)
  const [calibrationPoint, setCalibrationPoint] = useState(null)
  const [showShareModal, setShowShareModal] = useState(false)
  const [firmantesConTokens, setFirmantesConTokens] = useState([])
  const [documentoNombre, setDocumentoNombre] = useState('')
  const [requiereOrdenFirma, setRequiereOrdenFirma] = useState(false)
  const [enEsperaInfo, setEnEsperaInfo] = useState(null)
  const [rechazadoInfo, setRechazadoInfo] = useState(null)
  const [todosFiremantesInfo, setTodosFiremantesInfo] = useState(null)
  const fileInputRef = useRef(null)
  const [newsUiTick, setNewsUiTick] = useState(0)

  const uploadTourEnabled =
    !pdfFile && !(token || isSigningMode || isLoadingSigningDocument)
  // Mientras haya novedad pendiente sin snooze, no auto-disparar el tour completo del editor
  const newsBlocksAutoFullTour =
    !!pdfFile &&
    shouldShowNewsTour(NEWS_EDITOR_VIEWER_ZOOM) &&
    !isNewsEditorViewerZoomSnoozed()
  const editorTourEnabled = !!pdfFile && !newsBlocksAutoFullTour
  usePageTour('upload-page', { enabled: uploadTourEnabled })
  usePageTour('editor-page', { enabled: editorTourEnabled, syncKey: newsUiTick })

  useEffect(() => {
    const onNewsClosed = () => setNewsUiTick((n) => n + 1)
    window.addEventListener('app-news:closed', onNewsClosed)
    return () => window.removeEventListener('app-news:closed', onNewsClosed)
  }, [])

  // Sin recuperación de borradores: limpiar claves locales residuales al entrar
  useEffect(() => {
    clearAllPdfSignerEditorDrafts()
  }, [])

  // Restaurar estado del modal desde sessionStorage al montar el componente
  useEffect(() => {
    try {
      const savedShowModal = sessionStorage.getItem('pdfEditor_showShareModal')
      if (savedShowModal === 'true') {
        const savedFirmantes = sessionStorage.getItem('pdfEditor_firmantesConTokens')
        const savedNombre = sessionStorage.getItem('pdfEditor_documentoNombre')
        if (savedFirmantes) {
          console.log('[DEBUG PDFEditor] 🔄 Restaurando modal desde sessionStorage')
          showShareModalRef.current = true
          navigationBlockedRef.current = true
          setFirmantesConTokens(JSON.parse(savedFirmantes))
          setDocumentoNombre(savedNombre || 'Documento')
          setShowShareModal(true)
        }
      }
    } catch (e) {
      console.error('[DEBUG PDFEditor] Error al restaurar desde sessionStorage:', e)
    }
  }, []) // Solo ejecutar una vez al montar

  // Sincronizar el ref inmediatamente cuando cambia el state del modal
  useLayoutEffect(() => {
    showShareModalRef.current = showShareModal
    navigationBlockedRef.current = showShareModal
    if (showShareModal) {
      console.log('[DEBUG PDFEditor] 🔒 BLOQUEO DE NAVEGACIÓN ACTIVADO - modal abierto')
      // Prevenir cualquier navegación mientras el modal está abierto
      // Interceptar window.location también
      const originalPushState = window.history.pushState
      const originalReplaceState = window.history.replaceState
      
      window.history.pushState = function(...args) {
        if (showShareModalRef.current || navigationBlockedRef.current) {
          console.log('[DEBUG PDFEditor] 🚫 BLOQUEANDO history.pushState')
          return
        }
        return originalPushState.apply(window.history, args)
      }
      
      window.history.replaceState = function(...args) {
        if (showShareModalRef.current || navigationBlockedRef.current) {
          console.log('[DEBUG PDFEditor] 🚫 BLOQUEANDO history.replaceState')
          return
        }
        return originalReplaceState.apply(window.history, args)
      }
      
      return () => {
        window.history.pushState = originalPushState
        window.history.replaceState = originalReplaceState
      }
    } else {
      console.log('[DEBUG PDFEditor] 🔓 BLOQUEO DE NAVEGACIÓN DESACTIVADO - modal cerrado')
    }
  }, [showShareModal])

  // Escuchar cuando el modal se cierra desde GlobalShareModal
  useEffect(() => {
    const handleModalClosed = () => {
      // Cuando el modal se cierra desde GlobalShareModal, actualizar los refs
      showShareModalRef.current = false
      navigationBlockedRef.current = false
      setShowShareModal(false)
      console.log('[DEBUG PDFEditor] ✅ Refs actualizados después de cerrar modal')
    }
    
    window.addEventListener('pdfEditor_shareModalClosed', handleModalClosed)
    return () => {
      window.removeEventListener('pdfEditor_shareModalClosed', handleModalClosed)
    }
  }, [])

  // CRÍTICO: Efecto para mantener el modal abierto si el ref está true
  useEffect(() => {
    const savedShowModal = sessionStorage.getItem('pdfEditor_showShareModal')
    // Solo reactivar si sessionStorage dice que debe estar abierto
    if (savedShowModal === 'true' && showShareModalRef.current && !showShareModal) {
      console.log('[DEBUG PDFEditor] ⚠️ Modal se cerró pero el ref está true - REACTIVANDO...')
      const savedFirmantes = sessionStorage.getItem('pdfEditor_firmantesConTokens')
      const savedNombre = sessionStorage.getItem('pdfEditor_documentoNombre')
      if (savedFirmantes) {
        try {
          setFirmantesConTokens(JSON.parse(savedFirmantes))
          setDocumentoNombre(savedNombre || 'Documento')
          setShowShareModal(true)
          console.log('[DEBUG PDFEditor] ✅ Modal reactivado desde sessionStorage')
        } catch (e) {
          console.error('[DEBUG PDFEditor] Error al reactivar:', e)
        }
      }
    } else if (savedShowModal !== 'true' && showShareModalRef.current) {
      // Si sessionStorage dice que NO debe estar abierto, actualizar los refs
      showShareModalRef.current = false
      navigationBlockedRef.current = false
      setShowShareModal(false)
    }
  }, [showShareModal])

  useEffect(() => {
    // NO ejecutar si el modal de compartir está abierto (verificaciones múltiples)
    // También verificar sessionStorage
    const savedShowModal = sessionStorage.getItem('pdfEditor_showShareModal')
    if (navigationBlockedRef.current || showShareModalRef.current || showShareModal || savedShowModal === 'true') {
      console.log('[DEBUG PDFEditor] useEffect bloqueado - modal de compartir está abierto', {
        navigationBlockedRef: navigationBlockedRef.current,
        showShareModalRef: showShareModalRef.current,
        showShareModal: showShareModal,
        savedShowModal: savedShowModal
      })
      return
    }
    
    console.log('[DEBUG PDFEditor] useEffect ejecutándose - showShareModal:', showShareModal, 'ref:', showShareModalRef.current)
    
    // Si hay un token, cargar documento para firma
    if (token) {
      // Supervisor no puede firmar documentos compartidos
      if (isSupervisor) {
        toast.error('No tienes permisos para firmar documentos')
        if (!navigationBlockedRef.current && !showShareModalRef.current && !showShareModal) {
          safeNavigate('/dashboard', { replace: true })
        }
        return
      }
      loadDocumentForSigning(token)
    }
    // Si hay un archivo en el estado de navegación, usarlo
    else if (location.state?.file) {
      // Supervisor no puede subir documentos (solo administradores y usuarios pueden)
      if (isSupervisor) {
        toast.error('No tienes permisos para subir documentos')
        if (!navigationBlockedRef.current && !showShareModalRef.current && !showShareModal) {
          safeNavigate('/dashboard', { replace: true })
        }
        return
      }
      handleFileUpload(location.state.file)
      
      // Si estamos en modo calibración, mostrar información del punto
      if (location.state?.calibrationMode && location.state?.calibrationPoint) {
        const point = location.state.calibrationPoint
        setIsCalibrationMode(true)
        setCalibrationPoint(point)
        setTimeout(() => {
          toast(
            `Modo Calibración: Coloca la esquina superior izquierda de tu firma imagen exactamente sobre la X roja del ${point.label} (X=${point.x}, Y=${point.y})`,
            { duration: 8000, icon: 'ℹ️' }
          )
        }, 1000)
      }
      
      // Guardar fileId si viene del estado
      if (location.state?.fileId) {
        setFileId(location.state.fileId)
      }
    }
    // Si supervisor intenta acceder sin token ni archivo, redirigir
    else if (isSupervisor && !token && !location.state?.file) {
      toast.error('No tienes permisos para subir documentos')
      if (!navigationBlockedRef.current && !showShareModalRef.current && !showShareModal) {
        safeNavigate('/dashboard', { replace: true })
      }
    }
  }, [token, isSupervisor]) // Removido location.state de las dependencias para evitar re-ejecuciones

  const loadDocumentForSigning = async (token) => {
    try {
      setIsLoadingSigningDocument(true)
      const response = await fetch(`${API_BASE_URL}/api/firmar/${token}`)
      if (response.ok) {
        const data = await response.json()
        const bloqueo = interpretarRespuestaFirmante(data)
        if (bloqueo) {
          setIsLoadingSigningDocument(false)
          setSigningToken(token)
          if (bloqueo.tipo === 'en_espera') {
            setEnEsperaInfo({
              mensaje: bloqueo.mensaje,
              nombre_esperando: bloqueo.nombreEsperando,
              orden_firmante: bloqueo.ordenFirmante,
              total_firmantes: bloqueo.totalFirmantes,
              titulo_documento: bloqueo.tituloDocumento,
            })
            setRechazadoInfo(null)
          } else {
            setRechazadoInfo({
              titulo: bloqueo.titulo,
              mensaje: bloqueo.mensaje,
              motivo: bloqueo.motivo,
              titulo_documento: bloqueo.tituloDocumento,
            })
            setEnEsperaInfo(null)
          }
          return
        }
        setEnEsperaInfo(null)
        setRechazadoInfo(null)
        setFirmanteInfo(data.firmante_info)
        setTodosFiremantesInfo(data.firmantes_panel || null)
        setPoliticaFirma(
          data.politica_firma || data.documento?.politica_firma || null
        )
        setSigningToken(token)
        setIsSigningMode(true)
        
        let pdfUrl = data.documento?.ruta_pdf_firmado || data.documento?.ruta_pdf_original
        if (!pdfUrl) {
          setIsLoadingSigningDocument(false)
          toast.error('No se pudo cargar el documento. Falta la ruta del archivo.')
          return
        }
        
        pdfUrl = resolveFileUrl(pdfUrl)
        
        setPdfFile(pdfUrl)
        
        // Extraer la ruta del archivo de la URL para filePath
        let filePath = null
        if (pdfUrl.includes('/uploads/')) {
          // Si es una URL de uploads, extraer la ruta
          const urlParts = pdfUrl.split('/uploads/')
          if (urlParts.length > 1) {
            filePath = `uploads/${urlParts[1]}`
          }
        } else if (pdfUrl.includes('/signed_documents/')) {
          // Si es una URL de signed_documents, extraer la ruta
          const urlParts = pdfUrl.split('/signed_documents/')
          if (urlParts.length > 1) {
            filePath = `signed_documents/${urlParts[1]}`
          }
        } else {
          // Si es una URL de descarga, usar la URL completa como filePath
          filePath = pdfUrl.replace(`${API_BASE_URL}/`, '')
        }
        
        // Establecer ruta y cargar metadatos del PDF dentro del bloque exitoso
        setFilePath(filePath)
        
        // Cargar información del PDF
        try {
          const pdf = await pdfjsLib.getDocument(pdfUrl).promise
          setPdfDoc(pdf) // Guardar el objeto PDF completo
          setTotalPages(pdf.numPages)
          setCurrentPage(1)
          await mostrarCamposGuardados(pdf, data.campos_firma)
          await mostrarNotasGuardadas(pdf, data.notas)
          
          // No aplicar firma automáticamente - el usuario debe hacer clic en "Agregar Firma"
          toast.success('Documento cargado para firma')
        } catch (pdfError) {
          console.error('Error al cargar PDF:', pdfError)
          // Si el PDF original no se encuentra, intentar con el firmado o viceversa
          const alternativeUrl = data.documento.ruta_pdf_original || data.documento.ruta_pdf_firmado
          if (alternativeUrl && alternativeUrl !== pdfUrl) {
            const altPdfUrl = resolveFileUrl(alternativeUrl)
            try {
              const pdf = await pdfjsLib.getDocument(altPdfUrl).promise
              setPdfFile(altPdfUrl)
              setPdfDoc(pdf)
              setTotalPages(pdf.numPages)
              setCurrentPage(1)
              await mostrarCamposGuardados(pdf, data.campos_firma)
              await mostrarNotasGuardadas(pdf, data.notas)
              toast.success('Documento cargado para firma (usando archivo alternativo)')
            } catch (altError) {
              console.error('Error al cargar PDF alternativo:', altError)
              toast.error('No se pudo cargar el documento. El archivo no se encuentra en el servidor.')
              navigate('/documentos-pendientes')
            }
          } else {
            toast.error('No se pudo cargar el documento. El archivo no se encuentra en el servidor.')
            navigate('/documentos-pendientes')
          }
        }
      } else {
        const errBody = await response.json().catch(() => ({}))
        const parsed = parseApiDetail(errBody.detail, { httpStatus: response.status })
        setIsLoadingSigningDocument(false)
        setSigningToken(token)
        if (
          response.status === 410 ||
          response.status === 404 ||
          response.status === 403 ||
          response.status === 400 ||
          response.status >= 500
        ) {
          setRechazadoInfo({
            titulo:
              parsed.title ||
              (response.status === 404
                ? 'Enlace no válido'
                : response.status >= 500
                  ? 'No fue posible completar la operación'
                  : 'Documento no disponible'),
            mensaje: parsed.message || 'No se pudo abrir el documento para firmar.',
            motivo: null,
            titulo_documento: null,
          })
          setEnEsperaInfo(null)
          return
        }
        toast.error(parsed.message || 'Error al cargar el documento')
        return
      }
    } catch (error) {
      console.error('Error al cargar documento para firma:', error)
      toast.error('Error al cargar el documento')
    } finally {
      setIsLoadingSigningDocument(false)
    }
  }

  const mostrarCamposGuardados = async (pdf, camposApi) => {
    const visuales = []
    for (const campo of camposApi || []) {
      if (!campo || campo.pagina < 1 || campo.pagina > pdf.numPages) continue
      const pdfPage = await pdf.getPage(campo.pagina)
      const viewport = pdfPage.getViewport({ scale: 1.0 })
      const ratios = pdfALienzoFirma({
        coordenadas_x: campo.coordenadas_x,
        coordenadas_y: campo.coordenadas_y,
        ancho: campo.ancho,
        alto: campo.alto,
        pdfWidth: viewport.width,
        pdfHeight: viewport.height,
      })
      const estadoFirmante = (campo.estado_firmante || '').toLowerCase()
      visuales.push({
        id: campo.id,
        propio: !!campo.propio,
        bloqueado: true,
        page: campo.pagina,
        nombre: campo.nombre || 'Firmante',
        completado: !!campo.completado,
        estado_firmante: estadoFirmante,
        color: estadoFirmante === 'rechazado'
          ? '#b91c1c'
          : (campo.propio ? '#1d4ed8' : '#6b7280'),
        ...ratios,
      })
    }
    setCamposFirma(visuales)
  }

  const mostrarNotasGuardadas = async (pdf, notasApi) => {
    setNotasRegistradas(notasApi || [])
    const visuales = []
    for (const nota of notasApi || []) {
      if (!nota || nota.en_archivo || nota.pagina < 1 || nota.pagina > pdf.numPages) continue
      const pdfPage = await pdf.getPage(nota.pagina)
      const viewport = pdfPage.getViewport({ scale: 1.0 })
      const ratios = pdfALienzoFirma({
        coordenadas_x: nota.coordenadas_x,
        coordenadas_y: nota.coordenadas_y,
        ancho: nota.ancho,
        alto: nota.alto,
        pdfWidth: viewport.width,
        pdfHeight: viewport.height,
      })
      visuales.push({
        id: nota.id,
        texto: nota.texto,
        autorNombre: nota.autor_nombre || 'Autor',
        fechaCreacion: nota.fecha_creacion,
        page: nota.pagina,
        guardada: true,
        propia: !!nota.propia,
        ...ratios,
      })
    }
    setNotas(visuales)
  }

  const convertirNotaAPdf = async (nota) => {
    const pdfPage = await pdfDoc.getPage(nota.page)
    const viewport = pdfPage.getViewport({ scale: 1.0 })
    const scale = pdfScale || 1
    const canvasWidth = viewport.width * scale
    const canvasHeight = viewport.height * scale
    const convertido = lienzoFirmaAPdf({
      x: nota.xRatio * canvasWidth,
      y: nota.yRatio * canvasHeight,
      anchoLienzo: nota.wRatio * canvasWidth,
      altoLienzo: nota.hRatio * canvasHeight,
      pdfWidth: viewport.width,
      pdfHeight: viewport.height,
      scale,
    })
    return {
      pagina: nota.page,
      texto: nota.texto,
      coordenadas_x: convertido.coordenadas_x,
      coordenadas_y: convertido.coordenadas_y,
      ancho: convertido.ancho,
      alto: convertido.alto,
    }
  }

  const handleSigningMode = async () => {
    const modoCamposActivo = firmanteDebeUsarCampos()
    if (modoCamposActivo && !hayCamposPropiosPendientes()) {
      toast.error(
        'Este documento solo permite firmar en campos asignados y no tienes campos pendientes.'
      )
      return
    }
    const firmaParaEnviar = modoCamposActivo ? firmaSeleccionada : signatures[0]
    if (!firmaParaEnviar) {
      toast.error(
        modoCamposActivo
          ? 'Selecciona tu firma para aplicarla en los campos asignados'
          : 'Debe agregar una firma antes de continuar'
      )
      return
    }

    setIsSigning(true)
    let bloqueoMostrado = false
    try {
      const signature = firmaParaEnviar
      let originalX
      let originalY
      let signatureWidthPdf
      let signatureHeightPdf
      let paginaEnvio
      if (modoCamposActivo) {
        originalX = 0
        originalY = 0
        signatureWidthPdf = 1
        signatureHeightPdf = 1
        paginaEnvio = 1
      } else {
      const scale = pdfScale || 1
      let pdfPage = null
      if (pdfDoc) {
        pdfPage = await pdfDoc.getPage(signature.page || currentPage)
      }

      if (!pdfPage) {
        toast.error('No se pudo obtener la página del PDF para calcular las coordenadas')
        setIsSigning(false)
        return
      }

      const viewport = pdfPage.getViewport({ scale: 1.0 })
      const pdfWidth = viewport.width
      const pdfHeight = viewport.height
      const canvasWidth = viewport.width * scale
      const canvasHeight = viewport.height * scale

      if (!canvasWidth || !canvasHeight) {
        toast.error('No se pudo calcular el tamaño del lienzo del PDF')
        setIsSigning(false)
        return
      }

      // CRÍTICO: Obtener dimensiones del canvas (en píxeles)
      const signatureWidthCanvas = signature.width || (signature.type === 'imagen' ? 200 : 200)
      const signatureHeightCanvas = signature.height || (signature.type === 'imagen' ? 67 : 34)

      // CRÍTICO: Convertir coordenadas y dimensiones usando el scale
      // Para X: enviamos el borde IZQUIERDO del campo de firma (coordenada fija).
      const convertido = lienzoFirmaAPdf({
        x: signature.x,
        y: signature.y,
        anchoLienzo: signatureWidthCanvas,
        altoLienzo: signatureHeightCanvas,
        pdfWidth,
        pdfHeight,
        scale,
      })
      originalX = convertido.coordenadas_x
      originalY = convertido.coordenadas_y

      // ═══ DEBUG COORDENADAS ═══
      console.log('╔══════════════════════════════════════╗')
      console.log('║       COORDENADAS ENVIADAS AL PDF     ║')
      console.log('╚══════════════════════════════════════╝')
      console.log(`  Texto firma    : "${signature.text}"`)
      console.log(`  Largo texto    : ${(signature.text || '').length} caracteres`)
      console.log(`  signature.x    : ${signature.x}  (borde izquierdo div, px canvas)`)
      console.log(`  signature.width: ${signatureWidthCanvas}  (ancho div, px canvas)`)
      console.log(`  centro visual  : ${signature.x + signatureWidthCanvas / 2}  (px canvas)`)
      console.log(`  canvasWidth    : ${canvasWidth}`)
      console.log(`  pdfWidth       : ${pdfWidth}`)
      console.log(`  scale          : ${scale}`)
      console.log(`  originalX      : ${originalX}  ← X enviada al backend (puntos PDF)`)
      console.log(`  originalY      : ${originalY}  ← Y enviada al backend (puntos PDF)`)
      console.log('────────────────────────────────────────')
      
      // CRÍTICO: Convertir dimensiones del canvas a puntos PDF usando el scale
      // Si el canvas tiene scale=1.5, entonces 150 píxeles canvas = 100 puntos PDF
      signatureWidthPdf = convertido.ancho
      signatureHeightPdf = convertido.alto
      
      console.log(`[Firmante] Conversión de dimensiones:`)
      console.log(`  Canvas: ${signatureWidthCanvas}x${signatureHeightCanvas} píxeles`)
      console.log(`  Scale: ${scale}`)
      console.log(`  PDF: ${signatureWidthPdf}x${signatureHeightPdf} puntos`)

      console.log('[Firmante] Canvas:', {
        canvasWidth,
        canvasHeight,
        scale
      })
      console.log('[Firmante] Coordenadas canvas (x, y, h):', {
        x: signature.x,
        y: signature.y,
        height: signatureHeightCanvas
      })
      console.log('[Firmante] Coordenadas PDF enviadas:', {
        originalX,
        originalY,
        width: signatureWidthPdf,
        height: signatureHeightPdf
      })
      paginaEnvio = signature.page || currentPage
      }

      const formData = new FormData()
      
      // Solo enviar texto_firma si es de tipo texto
      // Para imágenes, no enviar texto_firma para que el backend use la imagen
      if (signature.type === 'text') {
        formData.append('texto_firma', signature.text || 'Firma')
        // Enviar la fuente si está disponible
        if (signature.font) {
          formData.append('signature_font', signature.font)
        }
      }
      // No agregar texto_firma si es imagen - el backend lo manejará
      
      formData.append('coordenadas_x', originalX.toString())
      formData.append('coordenadas_y', originalY.toString())
      formData.append('pagina', paginaEnvio.toString())
      formData.append('comentarios', '')
      
      // CRÍTICO: Agregar width y height si están disponibles (en puntos PDF)
      if (signatureWidthPdf) {
        formData.append('width', signatureWidthPdf.toString())
      }
      if (signatureHeightPdf) {
        formData.append('height', signatureHeightPdf.toString())
      }

      // Si es imagen, usar el archivo o la URL de la imagen configurada
      if (signature.type === 'imagen') {
        if (signature.imageFile) {
          formData.append('imagen_firma', signature.imageFile)
        } else if (signature.imageUrl) {
          // Si es una firma automática con imagen, necesitamos obtener el archivo desde la URL
          try {
            // El navegador agregará automáticamente el header Origin
            const imageResponse = await fetch(signature.imageUrl, {
              method: 'GET',
              mode: 'cors',
              credentials: 'omit'  // No incluir credenciales para evitar problemas CORS
            })
            if (imageResponse.ok) {
              const blob = await imageResponse.blob()
              const fileName = signature.imageUrl.split('/').pop() || 'signature.png'
              const file = new File([blob], fileName, { type: blob.type })
              formData.append('imagen_firma', file)
              console.log('[DEBUG] Imagen de firma obtenida desde URL y agregada al FormData')
            } else {
              console.error('[ERROR] No se pudo obtener la imagen desde la URL:', signature.imageUrl)
              throw new Error(`Error HTTP ${imageResponse.status}: No se pudo obtener la imagen`)
            }
          } catch (error) {
            console.error('Error al obtener imagen de firma:', error)
            throw new Error('No se pudo cargar la imagen de firma. Verifica que la imagen esté disponible.')
          }
        } else {
          console.warn('[WARN] Firma de tipo imagen pero no tiene imageFile ni imageUrl')
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/firmar/${signingToken}`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const parsed = parseApiDetail(errorData.detail, { httpStatus: response.status })
        // Mensaje funcional conocido o genérico seguro; no filtrar internos
        setRechazadoInfo({
          titulo: parsed.title || 'No es posible firmar',
          mensaje: parsed.message,
          motivo: null,
          titulo_documento: null,
        })
        setEnEsperaInfo(null)
        bloqueoMostrado = true
        throw new Error(parsed.message)
      }

      const result = await response.json()
      
      if (result.success) {
        toast.success('Documento firmado exitosamente')
        // Pasar información del documento para la página de confirmación
        navigate('/firma-exitosa', {
          state: {
            hash_documento: result.document_hash || result.hash_documento,
            download_url: result.download_url,
            firma_id: result.firma_id,
            documento_id: result.documento_id
          }
        })
      } else {
        toast.error('Error al firmar el documento')
      }
      
    } catch (error) {
      console.error('Error al firmar:', error)
      if (!bloqueoMostrado) {
        toast.error('Error al firmar: ' + (error.message || 'Error inesperado'))
      }
    } finally {
      setIsSigning(false)
    }
  }

  const loadSignatureConfig = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/config/signature`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        // La nueva API devuelve { signatures: [...], default_signature: {...} }
        return data
      }
      return null
    } catch (error) {
      console.error('Error al cargar configuración de firma:', error)
      return null
    }
  }

  const handleFileUpload = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer()

      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        body: formData,
      })

      if (!response.ok) {
        throw new Error('Error al subir el archivo')
      }

      const result = await response.json()

      setFileId(result.file_id)
      setFilePath(result.file_path)
      setPdfInfo(result.info)

      const pdf = await pdfjsLib.getDocument(arrayBuffer).promise
      setPdfDoc(pdf)
      setTotalPages(result.info.pages)
      setPdfFile(file)

      toast.success('PDF cargado exitosamente')
    } catch (error) {
      toast.error('Error al cargar el PDF: ' + error.message)
    }
  }

  const handleFileInput = (e) => {
    // Supervisor no puede subir documentos
    if (isSupervisor) {
      toast.error('No tienes permisos para subir documentos')
      e.target.value = '' // Limpiar el input
      return
    }
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0])
    }
  }

  const hayCamposPropiosPendientes = () => (
    isSigningMode && camposFirma.some((campo) => campo.propio && !campo.completado)
  )

  const esPoliticaCamposV1 = () => (
    String(politicaFirma || '').toLowerCase() === 'campos_v1'
  )

  /** Firmante: sin overlays libres si el documento exige campos o hay cajas propias. */
  const firmanteDebeUsarCampos = () => (
    isSigningMode && (esPoliticaCamposV1() || hayCamposPropiosPendientes())
  )

  const firmantesSinCampoPropio = () => {
    const conCampo = new Set(
      camposFirma
        .map((campo) => campo.tempFirmanteId)
        .filter(Boolean)
    )
    return firmantes.filter((firmante) => {
      const tid = firmante.temp_id
      return !tid || !conCampo.has(tid)
    })
  }

  const aplicarFirmaEnCampos = (signature) => {
    setFirmaSeleccionada({
      id: Date.now(),
      type: signature.type,
      text: signature.text,
      font: signature.font,
      imageUrl: signature.imageUrl,
      imageFile: signature.imageFile,
    })
    setSignatures([])
    setShowSignaturePanel(false)
    toast.success('Firma aplicada a tus campos')
  }

  const addSignature = (signature) => {
    if (firmanteDebeUsarCampos()) {
      if (!hayCamposPropiosPendientes() && esPoliticaCamposV1()) {
        toast.error(
          'Este documento solo permite firmar en los campos asignados. No hay campos pendientes para ti.'
        )
        return
      }
      aplicarFirmaEnCampos(signature)
      return
    }
    const newSignature = {
      id: Date.now(),
      ...signature,
      page: currentPage
    }
    setSignatures([...signatures, newSignature])
    setShowSignaturePanel(false)
    toast.success('Firma agregada')
  }

  const handleAddSignatureClick = async () => {
    // Cargar configuración de firma (ahora devuelve todas las firmas y la predeterminada)
    const signatureConfig = await loadSignatureConfig()
    
    if (signatureConfig && signatureConfig.default_signature) {
      const defaultSig = signatureConfig.default_signature
      
      if (defaultSig.signature_type === 'texto') {
        // Agregar firma de texto predeterminada
        const textSignature = {
          id: Date.now(),
          type: 'text',
          text: defaultSig.signature_text || `${user?.nombre || ''} ${user?.apellido || ''}`.trim(),
          font: defaultSig.signature_font || 'Dancing Script', // Incluir la fuente
          page: currentPage,
          x: 50,
          y: 50,
          width: 200,
          height: 34,  // 34px para acomodar 26px (1.6rem) con padding
          isAuto: false
        }
        if (firmanteDebeUsarCampos()) {
          if (!hayCamposPropiosPendientes() && esPoliticaCamposV1()) {
            toast.error(
              'Este documento solo permite firmar en los campos asignados. No hay campos pendientes para ti.'
            )
          } else {
            aplicarFirmaEnCampos(textSignature)
          }
        } else {
          setSignatures([...signatures, textSignature])
          toast.success('Firma predeterminada agregada')
        }
      } else if (defaultSig.signature_type === 'imagen' && defaultSig.signature_image_path) {
        // Construir URL completa de la imagen
        let imageUrl = defaultSig.signature_image_path
        if (!imageUrl.startsWith('http')) {
          if (!imageUrl.startsWith('/')) {
            imageUrl = `/${imageUrl}`
          }
          imageUrl = `${API_BASE_URL}${imageUrl}`
        }
        
        // Agregar la firma directamente sin verificación previa
        // Si la imagen no se puede cargar, el componente de imagen mostrará un error
        // Usar el tamaño guardado si está disponible, sino usar valores por defecto
        const imageSignature = {
          id: Date.now(),
          type: 'imagen',
          imageUrl: imageUrl,
          page: currentPage,
          x: 50,
          y: 50,
          width: defaultSig.signature_image_width || 200,
          height: defaultSig.signature_image_height || 67,
          isAuto: false
        }
        if (firmanteDebeUsarCampos()) {
          if (!hayCamposPropiosPendientes() && esPoliticaCamposV1()) {
            toast.error(
              'Este documento solo permite firmar en los campos asignados. No hay campos pendientes para ti.'
            )
          } else {
            aplicarFirmaEnCampos(imageSignature)
          }
        } else {
          setSignatures([...signatures, imageSignature])
          toast.success('Firma predeterminada agregada')
        }
      } else {
        // No hay firma predeterminada válida, abrir panel
        toast('No hay firma predeterminada configurada. Por favor, crea una firma.', { icon: 'ℹ️' })
        setShowSignaturePanel(true)
      }
    } else {
      // No hay configuración, abrir panel
      toast('No hay firma predeterminada configurada. Por favor, crea una firma.', { icon: 'ℹ️' })
      setShowSignaturePanel(true)
    }
  }

  const removeSignature = (signatureId) => {
    setSignatures(signatures.filter(sig => sig.id !== signatureId))
    toast.success('Firma eliminada')
  }

  const updateSignature = (signatureId, updatedSignature) => {
    setSignatures(signatures.map(sig => 
      sig.id === signatureId ? updatedSignature : sig
    ))
  }

  const showSendOutcomeToast = (result) => {
    const msg = result?.message || userMessageForStatus(result?.operationStatus, {
      authExpired: !!result?.authExpired,
    })
    const kind = result?.authExpired
      ? 'error'
      : toastKindForStatus(result?.operationStatus)
    if (kind === 'success') toast.success(msg)
    else if (kind === 'warning') toast(msg, { icon: '⚠️', duration: 6000 })
    else toast.error(msg)
  }

  /**
   * Envía el documento a firmantes. Devuelve resultado explícito (nunca [] como éxito).
   * El caller decide toasts/modal de compartir según operationStatus.
   */
  const sendNotificationsToSigners = async (documentUrl) => {
    if (firmantes.length === 0) {
      toast('No hay firmantes asignados para notificar', { icon: 'ℹ️' })
      return {
        ok: false,
        operationStatus: SEND_STATUS.REJECTED,
        firmantes: [],
        message: userMessageForStatus(SEND_STATUS.REJECTED),
      }
    }

    const faltanCampo = firmantesSinCampoPropio()
    if (faltanCampo.length > 0) {
      const nombres = faltanCampo
        .map((f) => f.nombre_completo || f.email || 'Firmante')
        .join(', ')
      const message = (
        `Debe ubicar al menos un campo de firma obligatorio por cada firmante. `
        + `Faltan campos para: ${nombres}.`
      )
      toast.error(message)
      return {
        ok: false,
        operationStatus: SEND_STATUS.REJECTED,
        firmantes: [],
        message,
      }
    }

    const token = localStorage.getItem('token') || sessionStorage.getItem('token')

    const camposEnvio = []
    const scale = pdfScale || 1
    for (const campo of camposFirma) {
      if (!pdfDoc) {
        return {
          ok: false,
          operationStatus: SEND_STATUS.REJECTED,
          firmantes: [],
          message: 'No se pudo leer el PDF para guardar los campos',
        }
      }
      if (!campo.tempFirmanteId) {
        return {
          ok: false,
          operationStatus: SEND_STATUS.REJECTED,
          firmantes: [],
          message: 'El campo no está asociado a un firmante del envío',
        }
      }
      const pdfPage = await pdfDoc.getPage(campo.page)
      const viewport = pdfPage.getViewport({ scale: 1.0 })
      const pdfWidth = viewport.width
      const pdfHeight = viewport.height
      const canvasWidth = pdfWidth * scale
      const canvasHeight = pdfHeight * scale
      const convertido = lienzoFirmaAPdf({
        x: campo.xRatio * canvasWidth,
        y: campo.yRatio * canvasHeight,
        anchoLienzo: campo.wRatio * canvasWidth,
        altoLienzo: campo.hRatio * canvasHeight,
        pdfWidth,
        pdfHeight,
        scale,
      })
      camposEnvio.push({
        temp_firmante_id: campo.tempFirmanteId,
        pagina: campo.page,
        coordenadas_x: convertido.coordenadas_x,
        coordenadas_y: convertido.coordenadas_y,
        ancho: convertido.ancho,
        alto: convertido.alto,
      })
    }
    if (!sendClientOperationIdRef.current) {
      sendClientOperationIdRef.current = createClientOperationId()
    }
    const requestBody = {
      file_id: fileId,
      firmantes: firmantes,
      visualizadores: visualizadores.length > 0 ? visualizadores : null,
      document_url: documentUrl || `${API_BASE_URL}/${filePath}`,
      document_name: pdfFile?.name || 'Documento',
      observaciones: '',
      requiere_orden_firma: requiereOrdenFirma,
      campos_firma: camposEnvio.length > 0 ? camposEnvio : null,
      notas: await Promise.all(notas.filter((nota) => !nota.guardada).map((nota) => convertirNotaAPdf(nota))),
      client_operation_id: sendClientOperationIdRef.current,
    }
    if (observaciones.trim() && notas.filter((nota) => !nota.guardada).length === 0) {
      toast('El texto sigue en la caja y no se colocó sobre el PDF, así que no viaja con el documento.', { icon: 'ℹ️' })
    }

    let result = await postSendNotifications({
      url: `${API_BASE_URL}/api/pdf/send-notifications`,
      token,
      body: requestBody,
      timeoutMs: 60000,
    })

    // Timeout / red: consultar GET (fingerprint lo valida el backend; no crear UUID nuevo)
    if (result?.operationStatus === SEND_STATUS.UNKNOWN && sendClientOperationIdRef.current) {
      try {
        const polled = await fetchSendOperationStatus({
          baseUrl: API_BASE_URL,
          clientOperationId: sendClientOperationIdRef.current,
          token,
        })
        if (polled?.documentRegistered || polled?.documentoId || polled?.operationStatus !== SEND_STATUS.UNKNOWN) {
          result = polled
        } else {
          result = { ...result, clientOperationId: sendClientOperationIdRef.current }
        }
      } catch {
        result = { ...result, clientOperationId: sendClientOperationIdRef.current }
      }
    }
    return result
  }

  const applySendOutcome = (result) => {
    showSendOutcomeToast(result)
    if (result?.documentoId) {
      registeredDocumentoIdRef.current = result.documentoId
    }
    if (shouldReuseClientOperationId(result?.operationStatus)) {
      // Mantener el mismo client_operation_id; no generar otro
      setSendBlockedUnknown(true)
      if (result?.operationStatus !== SEND_STATUS.UNKNOWN) {
        setSendRegisteredPendingSmtp(
          result?.operationStatus === SEND_STATUS.SMTP_PARTIAL
          || result?.operationStatus === SEND_STATUS.SMTP_FAILED
          || result?.operationStatus === SEND_STATUS.REGISTERED,
        )
      }
    }
    if (shouldBlockResend(result?.operationStatus)) {
      setSendBlockedUnknown(true)
    }
    // En éxito SMTP total: resetear estado de operación cliente (sin borradores locales)
    if (shouldClearEditorDraft(result?.operationStatus)) {
      sendClientOperationIdRef.current = null
      registeredDocumentoIdRef.current = null
      setSendRegisteredPendingSmtp(false)
      setSendBlockedUnknown(false)
    }
    const firmantesResult = result?.firmantes || []
    if (shouldOpenShareModal(result?.operationStatus, firmantesResult)) {
      showShareModalRef.current = true
      navigationBlockedRef.current = true
      try {
        const startTime = Date.now()
        sessionStorage.setItem('pdfEditor_showShareModal', 'true')
        sessionStorage.setItem('pdfEditor_firmantesConTokens', JSON.stringify(firmantesResult))
        sessionStorage.setItem('pdfEditor_documentoNombre', pdfFile?.name || 'Documento')
        sessionStorage.setItem('pdfEditor_modalStartTime', startTime.toString())
        if (result?.documentoId != null) {
          sessionStorage.setItem('pdfEditor_documentoId', String(result.documentoId))
        }
        window.dispatchEvent(new Event('pdfEditor_shareModalUpdated'))
      } catch (e) {
        console.error('[PDFEditor] Error al guardar estado de modal:', e)
      }
      setFirmantesConTokens(firmantesResult)
      setDocumentoNombre(pdfFile?.name || 'Documento')
      setShowShareModal(true)
      return true
    }
    // Error o unknown: no abrir modal; liberar bloqueo preventivo de navegación si se armó
    if (
      result?.operationStatus === SEND_STATUS.REJECTED ||
      result?.operationStatus === SEND_STATUS.UNKNOWN
    ) {
      showShareModalRef.current = false
      navigationBlockedRef.current = false
      try {
        sessionStorage.removeItem('pdfEditor_showShareModal')
        sessionStorage.removeItem('pdfEditor_firmantesConTokens')
      } catch { /* ignore */ }
    }
    return false
  }

  const handleSignDocument = async () => {
    if (sendInFlightRef.current || isSendingNotifications) {
      return
    }
    if (sendBlockedUnknown || sendRegisteredPendingSmtp) {
      toast(
        sendRegisteredPendingSmtp
          ? 'El documento ya quedó registrado. Use «Reintentar notificaciones» para correos pendientes o fallidos; no se creará otro documento.'
          : userMessageForStatus(SEND_STATUS.UNKNOWN),
        { icon: '⚠️', duration: 7000 },
      )
      return
    }

    // Si estamos en modo de firma, usar el endpoint de firma
    if (isSigningMode && signingToken) {
      if (isSupervisor) {
        toast.error('No tienes permisos para firmar documentos')
        return
      }
      await handleSigningMode()
      return
    }

    if (isSupervisor && !isSigningMode) {
      toast.error('No tienes permisos para subir o firmar documentos nuevos')
      return
    }

    // Documento ya firmado: solo notificar
    if (isDocumentSigned && signedDocumentUrl) {
      sendInFlightRef.current = true
      setIsSendingNotifications(true)
      try {
        const result = await sendNotificationsToSigners(signedDocumentUrl)
        applySendOutcome(result)
      } catch (error) {
        console.error('[PDFEditor] Error al enviar:', error)
        applySendOutcome({
          ok: false,
          operationStatus: SEND_STATUS.UNKNOWN,
          firmantes: [],
          message: userMessageForStatus(SEND_STATUS.UNKNOWN),
        })
      } finally {
        sendInFlightRef.current = false
        setIsSendingNotifications(false)
      }
      return
    }

    // Firmantes sin firmas propias: registrar documento + notificar
    if (firmantes.length > 0 && signatures.length === 0) {
      sendInFlightRef.current = true
      setIsSendingNotifications(true)
      const originalDocumentUrl = `${API_BASE_URL}/${filePath}`
      try {
        const result = await sendNotificationsToSigners(originalDocumentUrl)
        applySendOutcome(result)
      } catch (error) {
        console.error('[PDFEditor] Error al enviar:', error)
        applySendOutcome({
          ok: false,
          operationStatus: SEND_STATUS.UNKNOWN,
          firmantes: [],
          message: userMessageForStatus(SEND_STATUS.UNKNOWN),
        })
      } finally {
        // Mantener bloqueo hasta finalizar TODO el flujo (toasts/modal/estado)
        sendInFlightRef.current = false
        setIsSendingNotifications(false)
      }
      return
    }

    // Si hay firmantes y firmas, no permitir firmar (botón deshabilitado)
    if (firmantes.length > 0 && signatures.length > 0) {
      toast.error('No puedes firmar cuando hay firmantes asignados. Elimina los firmantes para firmar el documento.')
      return
    }

    // Si no hay firmas del usuario y no hay firmantes
    if (signatures.length === 0 && firmantes.length === 0) {
      toast.error('Agrega tu firma para continuar')
      return
    }

    setIsSigning(true)
    try {
      // Obtener dimensiones reales del PDF para convertir coordenadas correctamente
      const scale = pdfScale
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const headers = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // Firmar todas las firmas una por una, usando el archivo firmado previo como base
      let lastResult = null
      for (let i = 0; i < signatures.length; i++) {
        const signature = signatures[i]
        console.log(`[Editor] Firmando firma ${i + 1} de ${signatures.length}`)
      
      // Obtener la página del PDF para calcular dimensiones reales
      let pdfPage = null
      if (pdfDoc) {
          pdfPage = await pdfDoc.getPage(signature.page || currentPage)
      }
      
        let originalX = Math.round(signature.x / scale)
        let originalY = Math.round(signature.y / scale)
        
        // Calcular dimensiones de la firma
        // CRÍTICO: signature.width y signature.height ya están en puntos del canvas
        // Necesitamos convertirlos a puntos PDF usando el scale
        const signatureWidthCanvas = signature.width || (signature.type === 'imagen' ? 200 : 200)
        const signatureHeightCanvas = signature.height || (signature.type === 'imagen' ? 67 : 34)
      
      // Si tenemos información del PDF, usar las dimensiones reales del viewport
        let signatureWidthPdf = signatureWidthCanvas
        let signatureHeightPdf = signatureHeightCanvas
        
      if (pdfPage) {
          const viewport = pdfPage.getViewport({ scale: 1.0 })
        const pdfWidth = viewport.width
        const pdfHeight = viewport.height
          const canvasWidth = viewport.width * scale
          const canvasHeight = viewport.height * scale
        
        // Convertir coordenadas del canvas escalado a puntos del PDF
        // Para X: borde izquierdo fijo. El texto se extiende hacia la derecha.
          originalX = Math.round((signature.x / canvasWidth) * pdfWidth)
          originalY = Math.round((signature.y / canvasHeight) * pdfHeight)
          
          // CRÍTICO: Convertir tamaño de la firma del canvas al PDF
          // La firma en el canvas tiene dimensiones en píxeles del canvas
          // Necesitamos convertir a puntos PDF usando la relación de escala
          // signatureWidthCanvas está en píxeles del canvas, scale es la relación canvas/PDF
          signatureWidthPdf = Math.max(1, Math.round(signatureWidthCanvas / scale))
          signatureHeightPdf = Math.max(1, Math.round(signatureHeightCanvas / scale))
          
          console.log(`[Editor] Conversión de dimensiones:`)
          console.log(`  Canvas: ${signatureWidthCanvas}x${signatureHeightCanvas} píxeles`)
          console.log(`  Scale: ${scale}`)
          console.log(`  PDF: ${signatureWidthPdf}x${signatureHeightPdf} puntos`)
      } else {
        // Fallback: usar conversión simple (borde izquierdo)
          originalX = Math.round(signature.x / scale)
          originalY = Math.round(signature.y / scale)
          signatureWidthPdf = Math.max(1, Math.round(signatureWidthCanvas / scale))
          signatureHeightPdf = Math.max(1, Math.round(signatureHeightCanvas / scale))
          console.log(`[Editor] Conversión simple (sin PDF page): ${signatureWidthCanvas}x${signatureHeightCanvas} -> ${signatureWidthPdf}x${signatureHeightPdf}`)
      }
      
        console.log(`[Editor] Firma ${i + 1} - Coordenadas canvas: (${signature.x}, ${signature.y})`)
        console.log(`[Editor] Firma ${i + 1} - Coordenadas PDF enviadas: (${originalX}, ${originalY})`)
      
      const formData = new FormData()
      formData.append('file_id', fileId)
      formData.append('usuario', 'Usuario Demo')
        formData.append('nombre_firma', signature.text || 'Firma')
        formData.append('tipo_firma', signature.type === 'text' ? 'texto' : 'imagen')
      // Enviar la fuente si es firma de texto y está disponible
        if (signature.type === 'text' && signature.font) {
          formData.append('signature_font', signature.font)
      }
      formData.append('coordenadas_x', originalX.toString())
      formData.append('coordenadas_y', originalY.toString())
        formData.append('pagina', signature.page.toString())
      formData.append('ip', '127.0.0.1')
      
      // Agregar width y height si están disponibles (en puntos PDF)
      if (signatureWidthPdf) {
        formData.append('width', signatureWidthPdf.toString())
      }
      if (signatureHeightPdf) {
        formData.append('height', signatureHeightPdf.toString())
      }
        
        // Detectar si estamos en modo calibración (marcadores activos)
        // Verificar si la firma está cerca de un punto de referencia
        const referencePoints = [
          { id: 1, x: 100, y: 100 },
          { id: 2, x: 400, y: 100 },
          { id: 3, x: 100, y: 600 },
          { id: 4, x: 400, y: 600 }
        ]
        
        // Buscar el punto de referencia más cercano (dentro de 50 puntos)
        const threshold = 50
        let nearestPoint = null
        let minDistance = Infinity
        
        for (const point of referencePoints) {
          const distance = Math.sqrt(
            Math.pow(originalX - point.x, 2) + Math.pow(originalY - point.y, 2)
          )
          if (distance < threshold && distance < minDistance) {
            minDistance = distance
            nearestPoint = point
          }
        }
        
        // Si encontramos un punto cercano, enviar parámetros de calibración
        if (nearestPoint && signature.type === 'imagen') {
          formData.append('is_calibration_test', 'true')
          formData.append('calibration_point_id', nearestPoint.id.toString())
          formData.append('calibration_expected_x', nearestPoint.x.toString())
          formData.append('calibration_expected_y', nearestPoint.y.toString())
          console.log(`[CALIBRACIÓN] Firma detectada cerca del punto ${nearestPoint.id} (${nearestPoint.x}, ${nearestPoint.y})`)
        }

      // Si es imagen, usar el archivo o la URL de la imagen configurada
        if (signature.type === 'imagen') {
          if (signature.imageFile) {
            formData.append('signature_image', signature.imageFile)
            console.log('[DEBUG] Imagen de firma agregada desde imageFile')
          } else if (signature.imageUrl) {
            // Verificar si es un dataURL (base64) o una URL HTTP
            if (signature.imageUrl.startsWith('data:')) {
              // Es un dataURL (base64), convertir directamente a File
              try {
                const response = await fetch(signature.imageUrl)
                const blob = await response.blob()
                const file = new File([blob], 'signature.png', { type: blob.type || 'image/png' })
                formData.append('signature_image', file)
                console.log('[DEBUG] Imagen de firma convertida desde dataURL y agregada al FormData')
              } catch (error) {
                console.error('Error al convertir dataURL a archivo:', error)
                throw new Error('No se pudo procesar la imagen de firma.')
              }
            } else {
              // Es una URL HTTP, obtener el archivo desde la URL
              try {
                const imageResponse = await fetch(signature.imageUrl, {
              method: 'GET',
              mode: 'cors',
                  credentials: 'omit'
            })
            if (imageResponse.ok) {
              const blob = await imageResponse.blob()
                  const fileName = signature.imageUrl.split('/').pop() || 'signature.png'
                  const file = new File([blob], fileName, { type: blob.type || 'image/png' })
              formData.append('signature_image', file)
                  console.log('[DEBUG] Imagen de firma obtenida desde URL HTTP y agregada al FormData')
            } else {
                  console.error('[ERROR] No se pudo obtener la imagen desde la URL:', signature.imageUrl)
              throw new Error(`Error HTTP ${imageResponse.status}: No se pudo obtener la imagen`)
            }
          } catch (error) {
            console.error('Error al obtener imagen de firma:', error)
            throw new Error('No se pudo cargar la imagen de firma. Verifica que la imagen esté disponible.')
          }
        }
          } else {
            console.warn('[WARN] Firma de tipo imagen pero no tiene imageFile ni imageUrl')
            throw new Error('La firma de imagen no tiene archivo ni URL configurada')
      }
      }

        const response = await fetch(`${API_BASE_URL}/api/pdf/sign`, {
        method: 'POST',
        headers: headers,
        body: formData,
      })

      if (!response.ok) {
          const errorText = await response.text()
          console.error(`[ERROR] Error al firmar firma ${i + 1}:`, errorText)
          throw new Error(`Error al firmar la firma ${i + 1}: ${errorText}`)
        }

        lastResult = await response.json()
        console.log(`[Editor] Firma ${i + 1} completada exitosamente`)
      }

      // Si estamos en modo calibración, redirigir después de todas las firmas para ver los resultados
      if (isCalibrationMode) {
        toast.success('Prueba de calibración completada. Redirigiendo a la página de calibración para ver los resultados...')
        setTimeout(() => {
          navigate('/config/calibration')
        }, 1500)
        setIsSigning(false)
        return
      }

      const result = lastResult
      
      // Guardar información del documento firmado
      const documentUrl = `${API_BASE_URL}${result.download_url}`
      setSignedDocumentUrl(documentUrl)
      setDocumentHash(result.document_hash)
      setIsDocumentSigned(true)
      
      // Si hay firmantes, no permitir firmar (solo enviar sin firmar)
      // Este caso no debería ocurrir porque el botón está deshabilitado cuando hay firmantes y firmas
      if (firmantes.length > 0) {
        toast.error('No puedes firmar cuando hay firmantes asignados. Elimina los firmantes para firmar el documento.')
        setIsSigning(false)
        return
      }
      
      // Navegar a la página de confirmación con los datos del documento firmado
      toast.success('Documento firmado exitosamente')
      navigate('/firma-exitosa', {
        state: {
          hash_documento: result.document_hash || result.hash_documento,
          download_url: result.download_url,
          firma_id: result.firma_id,
          documento_id: result.documento_id,
          documento: {
            titulo: pdfInfo?.title || 'Documento',
            descripcion: pdfInfo?.subject || '',
            hash_documento: result.document_hash || result.hash_documento
          },
          firmante: {
            nombre: user?.nombre || '',
            apellido: user?.apellido || '',
            email: user?.email || ''
          },
          fechaFirma: new Date().toISOString()
        }
      })
      
    } catch (error) {
      toast.error('Error al firmar: ' + error.message)
    } finally {
      setIsSigning(false)
    }
  }

  const goBack = () => {
    navigate('/')
  }



  const handleDownload = () => {
    // Supervisor no puede descargar documentos
    if (isSupervisor) {
      toast.error('No tienes permisos para descargar documentos')
      return
    }
    if (signedDocumentUrl) {
      window.open(signedDocumentUrl, '_blank')
    }
  }

  const colorForEmail = (email) => {
    const palette = ['#1d4ed8', '#b45309', '#047857', '#7c3aed', '#be123c']
    const value = email || ''
    let index = 0
    for (let i = 0; i < value.length; i += 1) {
      index = (index + value.charCodeAt(i)) % palette.length
    }
    return palette[index]
  }

  const handleFirmantesChange = (nuevosFirmantes) => {
    const normalizados = nuevosFirmantes.map((firmante) => (
      firmante.temp_id ? firmante : { ...firmante, temp_id: generateTempId() }
    ))
    setFirmantes(normalizados)
    const ids = new Set(normalizados.map((firmante) => firmante.temp_id))
    setCamposFirma((prev) => prev.filter((campo) => ids.has(campo.tempFirmanteId)))
  }

  const crearCampoDesdeDrop = ({ email, nombre, tempFirmanteId, x, y, canvasWidth, canvasHeight }) => {
    const firmante = firmantes.find((item) => item.temp_id && item.temp_id === tempFirmanteId)
      || firmantes.find((item) => (item.email || '').toLowerCase() === (email || '').toLowerCase())
    if (!firmante || !firmante.temp_id || !canvasWidth || !canvasHeight) return
    const width = Math.min(180, canvasWidth)
    const height = Math.min(56, canvasHeight)
    const left = Math.max(0, Math.min(x - width / 2, canvasWidth - width))
    const top = Math.max(0, Math.min(y - height / 2, canvasHeight - height))
    setCamposFirma((prev) => [
      ...prev,
      {
        id: Date.now() + prev.length,
        tempFirmanteId: firmante.temp_id,
        firmanteEmail: firmante.email,
        etiqueta: firmante.nombre_completo || nombre || firmante.email,
        color: colorForEmail(firmante.email),
        page: currentPage,
        xRatio: left / canvasWidth,
        yRatio: top / canvasHeight,
        wRatio: width / canvasWidth,
        hRatio: height / canvasHeight
      }
    ])
  }

  const updateCampoFirma = (campoId, updated) => {
    setCamposFirma((prev) => prev.map((campo) => (campo.id === campoId ? updated : campo)))
  }

  const removeCampoFirma = (campoId) => {
    setCamposFirma((prev) => prev.filter((campo) => campo.id !== campoId))
  }

  const nombreAutorActual = () => {
    const nombre = `${user?.nombre || ''} ${user?.apellido || ''}`.trim()
    return nombre || 'Tú'
  }

  const crearNotaDesdeDrop = ({ texto, autorNombre, x, y, canvasWidth, canvasHeight }) => {
    const limpio = (texto || '').trim()
    if (!limpio || !canvasWidth || !canvasHeight) return
    const width = Math.min(240, canvasWidth)
    const height = Math.min(96, canvasHeight)
    const left = Math.max(0, Math.min(x - width / 2, canvasWidth - width))
    const top = Math.max(0, Math.min(y - height / 2, canvasHeight - height))
    setNotas((prev) => [
      ...prev,
      {
        id: `borrador-${Date.now()}-${prev.length}`,
        texto: limpio,
        autorNombre: autorNombre || nombreAutorActual(),
        page: currentPage,
        guardada: false,
        propia: true,
        xRatio: left / canvasWidth,
        yRatio: top / canvasHeight,
        wRatio: width / canvasWidth,
        hRatio: height / canvasHeight,
      }
    ])
  }

  const updateNota = (notaId, updated) => {
    setNotas((prev) => prev.map((nota) => (nota.id === notaId && !nota.guardada ? updated : nota)))
  }

  const removeNota = (notaId) => {
    setNotas((prev) => prev.filter((nota) => nota.id !== notaId || nota.guardada))
  }

  const guardarNotasFirmante = async () => {
    const borradores = notas.filter((nota) => !nota.guardada)
    if (!signingToken || borradores.length === 0) return
    setGuardandoNotas(true)
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      let siguientes = [...notas]
      for (const nota of borradores) {
        const cuerpo = await convertirNotaAPdf(nota)
        const response = await fetch(`${API_BASE_URL}/api/firmar/${signingToken}/notas`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
          },
          body: JSON.stringify(cuerpo),
        })
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.detail || 'No se pudo guardar la nota')
        }
        const guardada = await response.json()
        siguientes = siguientes.map((item) => item.id === nota.id ? {
          ...item,
          id: guardada.id,
          guardada: true,
          fechaCreacion: guardada.fecha_creacion,
          autorNombre: guardada.autor_nombre || item.autorNombre,
        } : item)
        setNotasRegistradas((prev) => prev.some((item) => item.id === guardada.id) ? prev : [...prev, guardada])
      }
      setNotas(siguientes)
      setObservaciones('')
      toast.success(borradores.length === 1 ? 'Nota guardada' : 'Notas guardadas')
    } catch (error) {
      toast.error(error.message || 'No se pudo guardar la nota')
    } finally {
      setGuardandoNotas(false)
    }
  }

  const handleVisualizadoresChange = (nuevosVisualizadores) => {
    setVisualizadores(nuevosVisualizadores)
  }

  const handleFinish = () => {
    setShowSuccessModal(false)
    setSignedDocumentUrl(null)
    setDocumentHash(null)
    setSignatures([])
    setFirmantes([])
    setCamposFirma([])
    setNotas([])
    setObservaciones('')
    setIsDocumentSigned(false)
    safeNavigate('/dashboard')
  }

  const handleViewerScaleChange = ({ scale }) => {
    if (typeof scale === 'number' && !Number.isNaN(scale)) {
      setPdfScale(scale)
    }
  }


  if (rechazadoInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{background: 'var(--background-color)'}}>
        <div className="max-w-lg w-full text-center bg-white shadow-sm rounded-2xl px-8 py-12 border border-red-100">
          <div className="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-6">
            <AlertTriangle className="w-7 h-7 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {rechazadoInfo.titulo || 'Documento rechazado'}
          </h2>
          <p className="text-gray-600 mb-4">
            {rechazadoInfo.titulo_documento && <span className="font-medium block mb-2">{rechazadoInfo.titulo_documento}</span>}
            {rechazadoInfo.mensaje}
          </p>
          {rechazadoInfo.motivo && (
            <p className="text-sm text-red-700 mb-6 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              Motivo: {rechazadoInfo.motivo}
            </p>
          )}
          <button
            type="button"
            onClick={() => navigate('/documentos-pendientes')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Volver a Mis Pendientes
          </button>
        </div>
      </div>
    )
  }

  if (enEsperaInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{background: 'var(--background-color)'}}>
        <div className="max-w-lg w-full text-center bg-white shadow-sm rounded-2xl px-8 py-12 border border-gray-100">
          <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-6">
            <Clock className="w-7 h-7 text-amber-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Documento en espera</h2>
          <p className="text-gray-600 mb-4">
            {enEsperaInfo.titulo_documento && <span className="font-medium block mb-2">{enEsperaInfo.titulo_documento}</span>}
            {enEsperaInfo.mensaje}
          </p>
          {enEsperaInfo.orden_firmante != null && enEsperaInfo.total_firmantes != null && (
            <p className="text-sm text-gray-500 mb-6">
              Firmante {enEsperaInfo.orden_firmante} de {enEsperaInfo.total_firmantes}
              {enEsperaInfo.nombre_esperando && ` · Esperando a: ${enEsperaInfo.nombre_esperando}`}
            </p>
          )}
          <button
            type="button"
            onClick={() => navigate('/documentos-pendientes')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Volver a Mis Pendientes
          </button>
        </div>
      </div>
    )
  }

  if (!pdfFile) {
    if (token || isSigningMode || isLoadingSigningDocument) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4" style={{background: 'var(--background-color)'}}>
          <div className="max-w-md w-full text-center bg-white shadow-sm rounded-2xl px-8 py-12 border border-gray-100">
            <div className="mx-auto w-12 h-12 border-4 border-primary-400 border-t-transparent rounded-full animate-spin mb-6"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Preparando documento para firma</h2>
            <p className="text-gray-600">
              Estamos cargando el documento que necesitas firmar. Este proceso puede tardar unos segundos.
            </p>
          </div>
        </div>
      )
    }
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Editor de PDF</h1>
        </div>

        <div className="card text-center py-12">
          <Upload className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Selecciona un archivo PDF
          </h2>
          <p className="text-gray-600 mb-6">
            Sube un documento PDF para comenzar a editarlo y firmarlo
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileInput}
            className="hidden"
            disabled={isSupervisor}
          />
          <button
            id="tour-upload-button"
            onClick={() => {
              if (isSupervisor) {
                toast.error('No tienes permisos para subir documentos')
                return
              }
              fileInputRef.current?.click()
            }}
            className="btn-primary"
            disabled={isSupervisor}
          >
            Seleccionar archivo PDF
          </button>
        </div>
        <HelpButton tourId="upload-page" id="tour-upload-help" />
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden" style={{background: 'var(--background-color)'}}>
      {/* Header */}
      <header className="main-header shrink-0">
        <div className="header-content">
          <div className="header-left">
            <div className="header-title">
              <h1>{isCalibrationMode ? 'Modo Calibración - Editor de PDF' : 'Editor de PDF'}</h1>
              <p>{pdfFile?.name || 'Cargar documento'} • {totalPages} páginas
                {isCalibrationMode && calibrationPoint && (
                  <span className="ml-2 text-orange-600 font-semibold">
                    • Punto: {calibrationPoint.label} (X={calibrationPoint.x}, Y={calibrationPoint.y})
                  </span>
                )}
              </p>
          </div>
        </div>

          <div className="header-right">
        </div>
      </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-4 py-4 sm:px-6 lg:flex-row lg:gap-6 lg:px-8">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="card flex h-full min-h-0 w-full min-w-0 flex-col">
            <EditorNewsBanner
              enabled={!!pdfFile}
              onUpdate={() => setNewsUiTick((n) => n + 1)}
            />
            <div className="flex min-h-0 w-full flex-1 flex-col">
              <PDFViewer
                file={pdfFile}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                totalPages={totalPages}
                signatures={
                  firmanteDebeUsarCampos()
                    ? []
                    : signatures.filter((sig) => sig.page === currentPage)
                }
                onRemoveSignature={firmanteDebeUsarCampos() ? undefined : removeSignature}
                onUpdateSignature={firmanteDebeUsarCampos() ? undefined : updateSignature}
                campos={camposFirma.filter((campo) => campo.page === currentPage)}
                onUpdateCampo={isSigningMode ? undefined : updateCampoFirma}
                onRemoveCampo={isSigningMode ? undefined : removeCampoFirma}
                onDropFirmante={isSigningMode ? undefined : crearCampoDesdeDrop}
                notas={notas}
                onUpdateNota={updateNota}
                onRemoveNota={removeNota}
                onDropNota={pdfFile ? crearNotaDesdeDrop : undefined}
                onScaleChange={handleViewerScaleChange}
                vistaFirma={isSigningMode ? firmaSeleccionada : null}
              />
            </div>
          </div>
        </div>

        <div className="flex min-h-0 w-full shrink-0 flex-col overflow-x-hidden overflow-y-auto max-h-[40%] lg:h-full lg:max-h-none lg:w-[340px] lg:min-w-[340px] lg:max-w-[340px]">
          {/* Botón de agregar firma - Solo disponible cuando se entra como firmante */}
          {isSigningMode && (
            <div style={{ order: -2 }}>
            <button
              onClick={handleAddSignatureClick}
              className="w-full mb-4 btn-secondary flex items-center justify-center gap-2"
            >
              <Upload className="h-5 w-5" />
              <span>Agregar Firma</span>
            </button>
            {(hayCamposPropiosPendientes() || esPoliticaCamposV1()) && (
              <p data-contador-campos="1" className="mb-3 text-sm font-medium text-gray-800">
                {esPoliticaCamposV1() && !hayCamposPropiosPendientes()
                  ? 'Este documento solo admite firma en campos asignados.'
                  : firmaSeleccionada
                  ? `${camposFirma.filter((campo) => campo.propio && !campo.completado).length} de ${camposFirma.filter((campo) => campo.propio && !campo.completado).length} campos listos`
                  : `Campos por completar: ${camposFirma.filter((campo) => campo.propio && !campo.completado).length}`}
              </p>
            )}
            </div>
          )}
          
          {/* Botón de acción principal - Alineado con los cards */}
          <button
            onClick={handleSignDocument}
            disabled={
              isSigning ||
              isSendingNotifications ||
              sendBlockedUnknown ||
              sendRegisteredPendingSmtp ||
              (isSigningMode && firmanteDebeUsarCampos() && !firmaSeleccionada) ||
              (isSigningMode && esPoliticaCamposV1() && !hayCamposPropiosPendientes()) ||
              (!isCalibrationMode && !isSigningMode && firmantes.length > 0 && signatures.length > 0) ||
              (!isCalibrationMode && !isSigningMode && signatures.length === 0 && firmantes.length === 0) ||
              (!isCalibrationMode && !isSigningMode && firmantes.length > 0 && firmantesSinCampoPropio().length > 0)
            }
            title={
              sendRegisteredPendingSmtp
                ? 'Documento ya registrado: use reintento de notificaciones'
                : sendBlockedUnknown
                ? 'Verifica el estado del documento antes de volver a enviar'
                : undefined
            }
            className="w-full mb-4 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSigning
              ? 'Firmando...'
              : isSendingNotifications
              ? 'Enviando...'
              : sendRegisteredPendingSmtp
              ? 'Documento registrado'
              : sendBlockedUnknown
              ? 'Verificar estado'
              : isCalibrationMode
              ? signatures.length > 0
                ? 'Firmar de Prueba (Ver Resultados)'
                : 'Agregar Firma de Prueba'
              : isSigningMode
              ? 'Firmar Documento'
              : firmantes.length > 0
              ? 'Enviar'
              : signatures.length > 0
              ? 'Firmar'
              : 'Selecciona Firmantes'}
          </button>
          {sendRegisteredPendingSmtp && sendClientOperationIdRef.current && (
            <button
              type="button"
              className="w-full mb-4 btn-secondary border border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100 rounded-lg px-3 py-2 text-sm"
              disabled={isSendingNotifications}
              onClick={async () => {
                if (sendInFlightRef.current) return
                sendInFlightRef.current = true
                setIsSendingNotifications(true)
                try {
                  const tokenAuth = localStorage.getItem('token') || sessionStorage.getItem('token')
                  const opId = sendClientOperationIdRef.current
                  const res = await fetch(
                    `${API_BASE_URL}/api/pdf/send-operations/${encodeURIComponent(opId)}/retry-notifications`,
                    {
                      method: 'POST',
                      headers: { Authorization: tokenAuth ? `Bearer ${tokenAuth}` : '' },
                    },
                  )
                  if (res.status === 401) {
                    applySendOutcome({
                      ok: false,
                      operationStatus: SEND_STATUS.REJECTED,
                      authExpired: true,
                      message: userMessageForStatus(SEND_STATUS.REJECTED, { authExpired: true }),
                      firmantes: [],
                    })
                    return
                  }
                  const payload = await res.json().catch(() => ({}))
                  if (!res.ok) {
                    toast.error(payload?.detail || 'No se pudieron reintentar las notificaciones')
                    return
                  }
                  applySendOutcome({
                    ...payload,
                    operationStatus: payload.operation_status,
                    documentoId: payload.documento_id,
                    firmantes: payload.firmantes || [],
                    message: userMessageForStatus(payload.operation_status),
                  })
                } catch (e) {
                  toast.error('Error de red al reintentar notificaciones')
                } finally {
                  sendInFlightRef.current = false
                  setIsSendingNotifications(false)
                }
              }}
            >
              Reintentar notificaciones (sin crear otro documento)
            </button>
          )}

          <div id="tour-document-info" className="card mb-4">
            <h3 className="font-semibold text-gray-900 mb-4">Información del Documento</h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-600">Páginas:</span>
                <span className="ml-2 font-medium">{totalPages}</span>
              </div>
              <div>
                <span className="text-gray-600">Página actual:</span>
                <span className="ml-2 font-medium">{currentPage}</span>
              </div>
              {pdfInfo?.title && (
                <div>
                  <span className="text-gray-600">Título:</span>
                  <span className="ml-2 font-medium">{pdfInfo.title}</span>
                </div>
              )}
            </div>
          </div>

          {/* Panel de estado de firmantes - Solo en modo firma */}
          {isSigningMode && todosFiremantesInfo && todosFiremantesInfo.length > 0 && (
            <div className="card mb-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <svg className="h-4 w-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Firmantes
                <span className="ml-auto text-xs font-normal text-gray-500">
                  {todosFiremantesInfo.filter(f => f.estado === 'firmado').length} de {todosFiremantesInfo.length} firmados
                </span>
              </h3>
              <div className="space-y-2">
                {todosFiremantesInfo.map((f) => (
                  <div
                    key={f.id}
                    className={`flex items-start gap-2 p-2 rounded-lg text-sm ${
                      f.es_actual
                        ? 'bg-blue-50 border border-blue-200'
                        : f.estado === 'firmado'
                        ? 'bg-green-50'
                        : 'bg-gray-50'
                    }`}
                  >
                    {/* Número de orden */}
                    <div
                      className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        f.estado === 'firmado'
                          ? 'bg-green-500 text-white'
                          : f.es_actual
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-300 text-gray-600'
                      }`}
                    >
                      {f.estado === 'firmado' ? (
                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        f.orden_firma || '?'
                      )}
                    </div>

                    {/* Info del firmante */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className={`font-medium truncate ${f.es_actual ? 'text-blue-800' : 'text-gray-800'}`}>
                          {f.nombre} {f.apellido}
                          {f.es_actual && <span className="text-blue-600 font-normal"> (tú)</span>}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 truncate">{f.email}</div>
                      {f.fecha_firma && (
                        <div className="text-xs text-green-600 mt-0.5">
                          {new Date(f.fecha_firma).toLocaleDateString('es-CO', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </div>
                      )}
                    </div>

                    {/* Badge de estado */}
                    <div className="flex-shrink-0">
                      {f.estado === 'rechazado' || f.estado_visual === 'rechazado' ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                          Rechazado
                        </span>
                      ) : f.estado === 'firmado' ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                          ✓ Firmado
                        </span>
                      ) : f.es_actual ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                          ⏳ Tú
                        </span>
                      ) : f.estado_visual === 'en_espera' ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                          ⏸️ Espera
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                          ⏳ Pendiente
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Barra de progreso */}
              {todosFiremantesInfo.length > 1 && (
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Progreso</span>
                    <span>
                      {todosFiremantesInfo.filter(f => f.estado === 'firmado').length}/{todosFiremantesInfo.length}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                      style={{
                        width: `${(todosFiremantesInfo.filter(f => f.estado === 'firmado').length / todosFiremantesInfo.length) * 100}%`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sección de Firmantes - Ocultar en modo firma si ya mostramos el panel arriba */}
          {(!isSigningMode || !todosFiremantesInfo) && (
          <div className="card">
            <FirmantesSelector
              onFirmantesChange={handleFirmantesChange}
              firmantes={firmantes}
              disabled={isSigningMode}
              onSearchFocus={() => setIsSearchingUsers(true)}
              onSearchBlur={() => setIsSearchingUsers(false)}
              requiereOrdenFirma={requiereOrdenFirma}
              onRequiereOrdenFirmaChange={setRequiereOrdenFirma}
              arrastrarCampos={!isSigningMode && !!pdfFile}
            />
          </div>
          )}

          {!isSigningMode && firmantes.length > 0 && (
            <div className="card mb-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Campos de firma</h3>
              <p className="text-xs text-gray-500 mb-3">
                Obligatorio para enviar. Arrastre cada firmante al PDF: debe haber al menos un campo por firmante.
              </p>
              {firmantesSinCampoPropio().length > 0 && (
                <p data-campos-faltantes="1" className="mb-3 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                  Faltan campos para:{' '}
                  {firmantesSinCampoPropio()
                    .map((f) => f.nombre_completo || f.email)
                    .join(', ')}
                </p>
              )}
              {camposFirma.length > 0 ? (
              <ul className="space-y-2">
                {camposFirma.map((campo, index) => (
                  <li key={campo.id} className="flex items-center justify-between text-xs border border-gray-200 rounded px-2 py-1">
                    <button
                      type="button"
                      className="text-left"
                      onClick={() => setCurrentPage(campo.page)}
                    >
                      <span style={{ color: campo.color }}>{index + 1}. {campo.etiqueta} · Firma</span>
                      <span className="text-gray-500"> · pág. {campo.page}</span>
                    </button>
                    <button type="button" className="text-red-600" onClick={() => removeCampoFirma(campo.id)}>Quitar</button>
                  </li>
                ))}
              </ul>
              ) : (
                <p className="text-xs text-gray-500">Aún no hay campos colocados sobre el PDF.</p>
              )}
            </div>
          )}

          {/* Sección de Visualizadores */}
          {!isSigningMode && (
            <div className="card">
              <VisualizadoresSelector
                onVisualizadoresChange={handleVisualizadoresChange}
                visualizadores={visualizadores}
                disabled={isSigningMode}
                onSearchFocus={() => setIsSearchingUsers(true)}
                onSearchBlur={() => setIsSearchingUsers(false)}
              />
            </div>
          )}

          {/* Campo de Observaciones/Notas */}
          <div id="tour-observaciones" className="card" style={isSigningMode ? { order: -1 } : undefined}>
            <h3 className="font-semibold text-gray-900 mb-1">Observaciones o Notas</h3>
            <p className="text-xs text-gray-500 mb-3">Escribe la nota y arrástrala al PDF. Queda en esa posición para quien abra el documento y también en el PDF firmado.</p>
            <textarea
              value={observaciones}
              onChange={(e) => {
                const value = e.target.value
                if (value.length <= 1000) {
                  setObservaciones(value)
                }
              }}
              placeholder="Escribe la nota y luego arrástrala sobre el documento..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400 resize-none"
              rows={4}
              maxLength={1000}
            />
            <div className="mt-2 flex justify-between items-center text-sm">
              <span className="text-gray-500">Máximo 1000 caracteres</span>
              <span className={`${observaciones.length > 900 ? 'text-orange-600' : 'text-gray-500'}`}>
                {observaciones.length}/1000
              </span>
            </div>
            <div
              className={`mt-3 rounded-lg border border-gray-200 bg-white px-3 py-2 ${observaciones.trim() && pdfFile ? 'cursor-grab active:cursor-grabbing' : 'opacity-50'}`}
              draggable={!!(observaciones.trim() && pdfFile)}
              onDragStart={(event) => {
                if (!observaciones.trim() || !pdfFile) {
                  event.preventDefault()
                  return
                }
                event.dataTransfer.effectAllowed = 'copy'
                event.dataTransfer.setData('application/x-pdfsigner-nota', JSON.stringify({
                  texto: observaciones.trim(),
                  autorNombre: nombreAutorActual(),
                }))
                event.dataTransfer.setData('text/plain', observaciones.trim())
              }}
            >
              <p className="text-[10px] font-semibold text-gray-800">Nota de {nombreAutorActual()}</p>
              <p className="text-sm text-gray-900 line-clamp-2">{observaciones.trim() || 'Escribe una nota para poder colocarla'}</p>
            </div>
            {isSigningMode && notas.some((nota) => !nota.guardada) && (
              <button
                type="button"
                onClick={guardarNotasFirmante}
                disabled={guardandoNotas}
                className="mt-3 w-full rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60"
              >
                {guardandoNotas ? 'Guardando nota...' : 'Guardar nota en el documento'}
              </button>
            )}
            {notasRegistradas.length > 0 && (
              <ul className="mt-3 border-t border-gray-200">
                {notasRegistradas.map((nota) => (
                  <li key={nota.id} className="border-b border-gray-200 py-2 text-sm text-gray-900 last:border-b-0">
                    <p className="font-semibold">Nota de {nota.autor_nombre || 'Autor'}</p>
                    <p className="mt-1 whitespace-pre-wrap">{nota.texto}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      Pág. {nota.pagina}
                      {nota.fecha_creacion ? ` · ${new Date(nota.fecha_creacion).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-xs text-gray-500">
              {notasRegistradas.length + notas.filter((nota) => !nota.guardada).length} en el documento
            </p>
          </div>
        </div>
      </div>

      {/* Signature Panel Modal */}
      {showSignaturePanel && (
        <SignaturePanel
          onAddSignature={addSignature}
          onClose={() => setShowSignaturePanel(false)}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                ¡Documento Firmado Exitosamente!
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                El documento ha sido firmado digitalmente y está listo para descargar.
              </p>
              {documentHash && (
                <div className="bg-gray-50 p-3 rounded mb-4">
                  <p className="text-xs text-gray-500 mb-1">Hash del documento:</p>
                  <p className="text-xs font-mono text-gray-700 break-all">
                    {documentHash}
                  </p>
                </div>
              )}
              <div className="flex space-x-3">
                <button
                  onClick={handleDownload}
                  className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSupervisor}
                  title={isSupervisor ? 'No tienes permisos para descargar documentos' : 'Descargar PDF'}
                >
                  <Download className="h-4 w-4 inline mr-2" />
                  Descargar PDF
                </button>
                <button
                  onClick={handleFinish}
                  className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Finalizar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <HelpButton tourId="editor-page" id="tour-editor-help" />
    </div>
  )
}

export default PDFEditor

